import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, RevealText } from "@/components/brand/Reveal";
import { BrandCard } from "@/components/brand/BrandCard";
import { Chip } from "@/components/brand/Chip";
import { blogPosts, BLOG_TAGS } from "@/content/blogPosts";
import { SITE_NAME, SITE_URL } from "@/content/siteConfig";

export default function BlogIndex() {
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const filteredPosts = useMemo(() => {
    const byTag = activeTag ? blogPosts.filter((post) => post.tags.includes(activeTag)) : blogPosts;
    const q = query.trim().toLowerCase();
    if (!q) return byTag;
    return byTag.filter((post) =>
      post.title.toLowerCase().includes(q) ||
      post.excerpt.toLowerCase().includes(q) ||
      post.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [activeTag, query]);

  return (
    <>
      <SEO
        title={`Blog - ${SITE_NAME}`}
        description="Plain, practical writing on shared-phone parenting and how CrenelView helps."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: `Blog - ${SITE_NAME}`,
          url: `${SITE_URL}/blog`,
          blogPost: blogPosts.map((post) => ({
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            url: `${SITE_URL}/blog/${post.slug}`,
            datePublished: post.date,
            dateModified: post.dateModified,
          })),
        }}
      />

      <section className="mx-auto max-w-4xl px-6 pt-14 pb-10 sm:pt-20">
        <RevealText lines={["Parenting & shared-phone blog."]} className="font-heading text-4xl sm:text-5xl lg:text-6xl" as="h1" />
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-6">
        <Reveal>
          <label htmlFor="blog-search" className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cvmuted" aria-hidden="true" />
            <input
              id="blog-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, e.g. bedtime, YouTube, spyware"
              data-testid="blog-search-input"
              className="w-full rounded-[14px] border border-ink/[0.08] bg-cvcard py-3 pl-11 pr-11 text-sm text-cvbody placeholder:text-cvmuted focus:outline-none focus:ring-2 focus:ring-periwinkle/60"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                data-testid="blog-search-clear"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-cvmuted hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </label>
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-6" data-testid="blog-topic-filter">
        <Reveal className="flex flex-wrap gap-2">
          <Chip active={activeTag === null} onClick={() => setActiveTag(null)} data-testid="blog-filter-all">
            All topics
          </Chip>
          {BLOG_TAGS.map((tag) => (
            <Chip
              key={tag}
              active={activeTag === tag}
              onClick={() => setActiveTag(tag)}
              data-testid={`blog-filter-${tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {tag}
            </Chip>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20 sm:pb-28">
        {filteredPosts.length === 0 ? (
          <p className="text-base text-cvmuted" data-testid="blog-search-no-results">
            No articles match "{query}". Try a different search term.
          </p>
        ) : (
          <div className="flex flex-col gap-5" data-testid="blog-post-list">
            {filteredPosts.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08}>
                <Link to={`/blog/${post.slug}`} data-testid={`blog-post-link-${post.slug}`}>
                  <BrandCard className="transition-transform duration-200 ease-out hover:-translate-y-0.5">
                    <p className="text-sm text-cvmuted">
                      {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} - {post.readTime}
                    </p>
                    <h2 className="mt-2 font-heading text-2xl sm:text-3xl">{post.title}</h2>
                    <p className="mt-3 text-base text-cvbody">{post.excerpt}</p>
                    <span className="mt-4 inline-block text-sm font-semibold text-cvlink">Read more -&gt;</span>
                  </BrandCard>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
